import { APP_WEB_DOMAIN_LABELS, HERO_STYLE, HERO_STYLES, type HeroStyle } from '@/lib/constants/app';
import { HOME_LAYOUT, type HomeLayout } from '@/lib/constants/appPreview';
import type { AppCfgsDto } from '@/lib/types/App';
import type { AppV1Browse, AppV1Cfg, AppV1Item, AppV1Row } from '@/lib/types/AppV1';

export type { HeroStyle };

/** The shell's row type for the spotlight billboard (AppV1Assembler.composeRows). */
export const HERO_BILLBOARD_ROW_TYPE = 'hero_billboard';

/** The image route's "serve the poster as uploaded" switch (never the derived hero composition). */
export const RAW_ARTWORK_PARAM = 'src=raw';

/** ADR-0013 logo slot at FHD: at most 500 wide x 160 tall at the 96 margin, bottom on the title's bottom. */
export const HERO_LOGO_BOX_W = 500;
export const HERO_LOGO_BOX_H = 160;

/** The billboard band's height, the screen's size and the NavBar strip, at FHD. */
export const BILLBOARD_HEIGHT_FHD = 648;
export const SCREEN_WIDTH_FHD = 1920;
export const SCREEN_HEIGHT_FHD = 1080;
export const NAVBAR_HEIGHT_FHD = 120;

/**
 * The three hero styles at FHD (design ADR-0016 D3; surfaces.md "Hero
 * billboard"). Every style draws the item's RAW poster; the band is y 0–648.
 */
export const HERO_GEOMETRY = {
  /** The poster whole, 960x540, right edge x=1824, bottom edge y=648: crisp, square corners, no underlay. */
  card: { left: 864, top: 108, w: 960, h: 540 },
  /**
   * The poster scaled to 1280x720 and cropped to the band (its middle 648 rows,
   * 36 px trimmed top and bottom), anchored right (x 640–1920); its left edge
   * melts into the ground over 360 px (alpha 0 at x=640 → 1 at x=1000) and its
   * bottom over the band's last 180 px (y 468 → 648).
   */
  immersive: { left: 640, top: -36, w: 1280, h: 720, meltLeft: 360, meltBottom: 180 },
  /**
   * The poster scaled to the full width (1920x1080) and cropped to the band
   * (216 px trimmed top and bottom); a left gradient (ground 100 % at x=0 → 0 %
   * at x=1100) and the bottom melt from y=400 to 648.
   */
  full_bleed: { left: 0, top: -216, w: 1920, h: 1080, gradientWidth: 1100, meltTop: 400 },
} as const;

/**
 * The ONE page scrim over a full-screen picture (the Home image with rows under
 * it), static and under all content: the page colour at 0 % at y=180 → 40 %
 * at y=648 → 85 % at y=1080 (surfaces.md "The page scrim"). Never in
 * `billboard`, never over the plain ground.
 */
export const PAGE_SCRIM_TOP_FHD = 180;
export const PAGE_SCRIM_MID_ALPHA = 0.4;
export const PAGE_SCRIM_BOTTOM_ALPHA = 0.85;
/**
 * The ONE left scrim over a full-screen picture: static, full height, 960 wide
 * at the origin, the page colour from 100 % at x=0 to 0 % at x=960 on a
 * smoothstep (Roku device reference, 2026-10-01). It carries the metadata block.
 */
export const LEFT_SCRIM_WIDTH_FHD = 960;

/** The hero-state nav gradient (design ADR-0012): opaque at the screen edge, gone by 280. */
export const NAV_SCRIM_HEIGHT_FHD = 280;

/**
 * The content-bounded slide (design ADR-0016 D1): Down from the hero moves the
 * page by what the content needs, never more than the full 520 slide. Rows sit
 * at the 700 origin on a 40 px spacing; a row's block is its label line (50),
 * its art and the 2-line caption block (70) when titles sit under the art.
 */
export const FULL_SLIDE_FHD = 520;
export const ROW_ORIGIN_FHD = 700;
export const ROW_SPACING_FHD = 40;
export const ROW_LABEL_FHD = 50;
export const ROW_CAPTION_BLOCK_FHD = 70;
export const CONTENT_BOTTOM_MARGIN_FHD = 40;

/** What the billboard band draws as its picture: a style's art, or text only while a full-screen picture shows. */
export type HeroArt = HeroStyle | 'none';

/** The full-screen picture under the home: the customer's Home image, or the one-item title page's poster. */
export interface HomePicture {
  url: string;
  kind: 'home_image' | 'poster';
}

/** Pure mapping logic for the in-dashboard TV preview (vitest-covered). */
export class AppPreviewUtils {
  /**
   * The app's public domain — the /app/v1 tenancy key. Custom domain wins,
   * matching the server's resolution order; null when the app has no web row
   * (no tenancy anchor) or an unknown shared domain.
   */
  static appDomain(app: AppCfgsDto): string | null {
    const web = app.web;
    if (!web) return null;
    if (web.core.customDomain) return web.core.customDomain;
    const label = APP_WEB_DOMAIN_LABELS[web.core.sharedDomainId];
    return label && web.core.customName ? `${web.core.customName}.${label}` : null;
  }

  /** Artwork URL for a row's aspect, with the contract's card fallback chain. */
  static artworkUrl(item: AppV1Item, itemAspect: string): string | null {
    const a = item.artwork;
    switch (itemAspect) {
      case '2x3':
        return a.poster2x3 ?? a.card ?? null;
      case '1x1':
        return a.square ?? a.card ?? null;
      case 'banner':
        return a.banner ?? a.card ?? null;
      default:
        return a.card ?? a.poster2x3 ?? null;
    }
  }

  /**
   * The billboard's art (design ADR-0016 D3): the item's RAW poster — the
   * same asset the row tile uses, served as uploaded (`src=raw`), never the
   * derived hero composition or an ambient. Null = the item has no poster.
   */
  static rawPosterUrl(item: Pick<AppV1Item, 'artwork'>): string | null {
    const url = item.artwork.card ?? item.artwork.hero ?? null;
    return url ? AppPreviewUtils.rawArtworkUrl(url) : null;
  }

  /** Ask the image route for the asset as uploaded: only /app/img/ URLs understand `src=raw`. */
  static rawArtworkUrl(url: string): string {
    if (!url.includes('/app/img/')) return url;
    return url + (url.includes('?') ? '&' : '?') + RAW_ARTWORK_PARAM;
  }

  /**
   * Ask the image route for a display-sized rendition (rokucore SizedArtworkUri):
   * only /app/img/ URLs understand `w`; `raw` bypasses the derived hero crop so
   * the customer's complete composition renders whole.
   */
  static sizedArtworkUrl(url: string, width: number, raw = false): string {
    if (!url.includes('/app/img/')) return url;
    const params = raw ? `w=${width}&${RAW_ARTWORK_PARAM}` : `w=${width}`;
    return url + (url.includes('?') ? '&' : '?') + params;
  }

  /** The app's stored hero style on the wire; card is the absent / unknown-value fallback (design ADR-0016 D3). */
  static heroStyle(row: Pick<AppV1Row, 'heroStyle'>): HeroStyle {
    return (HERO_STYLES as readonly string[]).includes(row.heroStyle ?? '') ? (row.heroStyle as HeroStyle) : HERO_STYLE.CARD;
  }

  /**
   * Title slot rule (design ADR-0013): the series' `artwork.logo` REPLACES
   * the typed title once the image has loaded; the text title renders until
   * then and whenever the logo is absent or failed. Never both.
   */
  static heroTitleSlot(item: Pick<AppV1Item, 'artwork'>, logoLoaded: boolean): 'logo' | 'text' {
    return item.artwork.logo && logoLoaded ? 'logo' : 'text';
  }

  /**
   * Description cap: 2 lines while a logo or the meta line shows (they keep
   * the CTA inside the 648 band); 3 under a bare text title.
   */
  static heroDescriptionLines(slot: 'logo' | 'text', hasMetaLine = false): number {
    return slot === 'logo' || hasMetaLine ? 2 : 3;
  }

  /** The billboard's CTA says what happens (design ADR-0014): a video or stream plays, a series opens. */
  static heroCtaLabel(kind: string): 'Watch' | 'View' {
    return kind === 'media' || kind === 'live' ? 'Watch' : 'View';
  }

  // ── Home layouts (design ADR-0015), the picture and the slide (design ADR-0016) ──

  /**
   * The layout the SERVER chose (it alone counts items and rows). Absent or
   * unknown ⇒ rails — today's home (fallback discipline); a `billboard`
   * without a hero row to draw is rails too: the preview renders what is on
   * the wire, it never invents a billboard.
   */
  static homeLayout(browse: Pick<AppV1Browse, 'homeLayout' | 'rows'>): HomeLayout {
    switch (browse.homeLayout) {
      case HOME_LAYOUT.EMPTY:
        return HOME_LAYOUT.EMPTY;
      case HOME_LAYOUT.BILLBOARD:
        return AppPreviewUtils.heroRow(browse.rows) ? HOME_LAYOUT.BILLBOARD : HOME_LAYOUT.RAILS;
      case HOME_LAYOUT.ONE_SCREEN:
        return HOME_LAYOUT.ONE_SCREEN;
      default:
        return HOME_LAYOUT.RAILS;
    }
  }

  /**
   * The rows a layout draws: `empty` none; `billboard` the hero row only (the
   * live and category rows stay on the wire for shells that predate the field
   * and are NOT rendered); otherwise every non-empty row, as before.
   */
  static layoutRows(layout: HomeLayout, browse: AppV1Browse): AppV1Row[] {
    if (layout === HOME_LAYOUT.EMPTY) return [];
    const rows = AppPreviewUtils.visibleRows(browse);
    if (layout === HOME_LAYOUT.BILLBOARD) {
      const hero = AppPreviewUtils.heroRow(rows);
      return hero ? [hero] : [];
    }
    return rows;
  }

  /**
   * The full-screen picture under the home (design ADR-0016 D4 / D5), or null
   * for the plain ground: the customer's Home image whenever it is on (every
   * layout); else, in `billboard` under Immersive or Full-bleed, the one item's
   * raw poster covering the screen. Under Card the card stays a card — a
   * lettered poster never fills the screen.
   */
  static homePicture(
    layout: HomeLayout,
    style: HeroStyle,
    heroItem: Pick<AppV1Item, 'artwork'> | null | undefined,
    homeImageUrl: string | null,
  ): HomePicture | null {
    if (homeImageUrl) return { url: homeImageUrl, kind: 'home_image' };
    if (layout !== HOME_LAYOUT.BILLBOARD || style === HERO_STYLE.CARD || !heroItem) return null;
    const url = AppPreviewUtils.rawPosterUrl(heroItem);
    return url ? { url, kind: 'poster' } : null;
  }

  /**
   * What the band draws (design ADR-0016 D5): while a full-screen picture
   * shows, TEXT ONLY — no card, no immersive art, no underlay; otherwise the
   * style's art on the plain ground.
   */
  static heroArt(style: HeroStyle, pictureShown: boolean): HeroArt {
    return pictureShown ? 'none' : style;
  }

  /** The page scrim is drawn over a picture only when rows sit on it (never in `billboard`). */
  static pageScrimShown(picture: HomePicture | null, railRowCount: number): boolean {
    return picture != null && railRowCount > 0;
  }

  /** The app's Home image when the server emits one (`cfg.theme.backdrop`); absent = off. */
  static backdropUrl(cfg: Pick<AppV1Cfg, 'theme'>): string | null {
    return cfg.theme.backdrop?.url?.trim() || null;
  }

  /**
   * The content-bounded slide in FHD pixels (design ADR-0016 D1):
   * `clamp(contentBottom − 1080, 0, 520)`, where `contentBottom` is the bottom
   * of the LAST rendered rail's block (label, art, the full 2-line caption
   * block) plus the 40 px margin, rails at the 700 origin on the row pitch.
   * `below_card` 16:9 rails: 1 row → 0 (nothing moves), 2 rows → 308, 3+ → 520.
   * The rows counted are the rows the shell RENDERS (the local Continue
   * Watching row and the on-air Live Now row are ordinary rows); this is the
   * shell's slide, not the server's layout rule.
   */
  static slideFhd(railRows: readonly Pick<AppV1Row, 'itemAspect' | 'titleTreatment'>[]): number {
    if (railRows.length === 0) return 0;
    let top = ROW_ORIGIN_FHD;
    for (let i = 0; i < railRows.length - 1; i++) {
      top += AppPreviewUtils.rowHeightFhd(railRows[i]) + ROW_SPACING_FHD;
    }
    const last = railRows[railRows.length - 1];
    const block = ROW_LABEL_FHD + AppPreviewUtils.cardSizeFhd(last.itemAspect).h + (last.titleTreatment === 'below_card' ? ROW_CAPTION_BLOCK_FHD : 0);
    const contentBottom = top + block + CONTENT_BOTTOM_MARGIN_FHD;
    return Math.max(0, Math.min(FULL_SLIDE_FHD, contentBottom - SCREEN_HEIGHT_FHD));
  }

  /** The hero (and the picture under it) fades out only on a FULL slide; under a partial one it stays, cut by the nav strip. */
  static heroFadedOut(slidFhd: number): boolean {
    return slidFhd >= FULL_SLIDE_FHD;
  }

  /**
   * The page scrim's ramp over its own span (y=180 → 1080): clear at its top,
   * 40 % at the band's bottom (y=648), 85 % at the screen's bottom.
   */
  static pageScrimGradient(color: string): string {
    const mid = Number((((BILLBOARD_HEIGHT_FHD - PAGE_SCRIM_TOP_FHD) / (SCREEN_HEIGHT_FHD - PAGE_SCRIM_TOP_FHD)) * 100).toFixed(2));
    const a = (alpha: number) => AppPreviewUtils.withAlpha(color, alpha);
    return `linear-gradient(to bottom, ${a(0)} 0%, ${a(PAGE_SCRIM_MID_ALPHA)} ${mid}%, ${a(PAGE_SCRIM_BOTTOM_ALPHA)} 100%)`;
  }

  /** The smoothstep `t²(3 − 2t)` on [0, 1]. */
  static smoothstep(t: number): number {
    const c = Math.min(1, Math.max(0, t));
    return c * c * (3 - 2 * c);
  }

  /**
   * The left scrim's strength at `x` (FHD pixels from the left edge):
   * `t = 1 − x/960`, strength `t²(3 − 2t)` — 1 at 0, 0.84 at 240, 0.5 at 480,
   * 0.16 at 720, 0 from 960 on.
   */
  static leftScrimStrength(x: number): number {
    return AppPreviewUtils.smoothstep(1 - x / LEFT_SCRIM_WIDTH_FHD);
  }

  /** The left scrim as a gradient over its own 960-wide box: the page colour by the strength. */
  static leftScrimGradient(color: string): string {
    return AppPreviewUtils.edgeMeltGradient(color, 'to right');
  }

  /** The hero-state nav gradient (ADR-0012): the page colour, opaque at the screen edge and gone by its height. */
  static navScrimGradient(color: string): string {
    return `linear-gradient(to bottom, ${AppPreviewUtils.withAlpha(color, 1)}, ${AppPreviewUtils.withAlpha(color, 0)})`;
  }

  /**
   * Rendered logo size in FHD pixels: scaled to fit the slot's 500 x 160 box
   * and never enlarged past the asset's native size (a 300 x 120 mark stays
   * 300 x 120). The shells apply the same rule; the box is the series
   * header's ratified geometry.
   */
  static heroLogoSize(naturalW: number, naturalH: number): { w: number; h: number } {
    if (naturalW <= 0 || naturalH <= 0) return { w: 0, h: 0 };
    const scale = Math.min(HERO_LOGO_BOX_W / naturalW, HERO_LOGO_BOX_H / naturalH, 1);
    return { w: Math.round(naturalW * scale), h: Math.round(naturalH * scale) };
  }

  /** The spotlight row the shell diverts to the billboard (first with items). */
  static heroRow(rows: AppV1Row[]): AppV1Row | null {
    return (
      rows.find((row) => row.rowType === HERO_BILLBOARD_ROW_TYPE && row.items.length > 0) ??
      null
    );
  }

  /** Everything the shell renders as rails: non-empty rows minus the billboard. */
  static railRows(rows: AppV1Row[]): AppV1Row[] {
    return rows.filter((row) => row.rowType !== HERO_BILLBOARD_ROW_TYPE && row.items.length > 0);
  }

  /**
   * Rows worth rendering in a static preview: viewer-scoped rows (continue
   * watching) and off-air live rails arrive empty without a viewer session —
   * drop anything with no items rather than paint empty shelves.
   */
  static visibleRows(browse: AppV1Browse): AppV1Row[] {
    return browse.rows.filter((row) => row.items.length > 0);
  }

  /**
   * Tile art size in FHD pixels per row aspect (rokucore Rows.bs CardSizeFor);
   * the shell's 16:9 default is 320x180.
   */
  static cardSizeFhd(itemAspect: string): { w: number; h: number } {
    switch (itemAspect) {
      case '2x3':
        return { w: 200, h: 300 };
      case '1x1':
        return { w: 180, h: 180 };
      case 'banner':
        return { w: 504, h: 180 };
      default:
        return { w: 320, h: 180 };
    }
  }

  /** Row pitch inputs (rokucore HomeView): art height + below-card title block + 24px pad. */
  static rowHeightFhd(row: Pick<AppV1Row, 'itemAspect' | 'titleTreatment'>): number {
    const size = AppPreviewUtils.cardSizeFhd(row.itemAspect);
    const labelSpace = row.titleTreatment === 'below_card' ? 104 : 0;
    return size.h + labelSpace + 24;
  }

  /**
   * Hero-state rows origin — port of rokucore Rows.bs HeroRowsY (the
   * "second-row peek"): with 2+ rows the origin drops so row 2's top lands at
   * 1004, clamped to [672, 700]; single rows and rows too tall to peek keep 700.
   */
  static heroRowsY(rowHeights: number[], rowCount: number): number {
    const DEFAULT = ROW_ORIGIN_FHD;
    const MIN = 672;
    const PEEK_TOP = 1004;
    if (rowCount < 2 || rowHeights.length === 0) return DEFAULT;
    const pitch = rowHeights[0] + ROW_SPACING_FHD;
    let y = PEEK_TOP - pitch;
    if (y > DEFAULT) return DEFAULT;
    if (y < MIN) y = MIN;
    if (y + pitch >= SCREEN_HEIGHT_FHD) return DEFAULT;
    return y;
  }

  /**
   * FHD design pixels → container-query width units, so the simulated TV keeps
   * the shell's 1920-wide geometry at any preview width (1cqw = 1% of the frame).
   */
  static fhd(px: number): string {
    return `${Number((px / 19.2).toFixed(4))}cqw`;
  }

  /** `#rrggbb` (or `#rgb`) → `rgba(r, g, b, alpha)`; other inputs pass through. */
  static withAlpha(hex: string, alpha: number): string {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return hex;
    let h = m[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }

  /**
   * A melt into the page colour on a smoothstep: the colour fully opaque at
   * the gradient's start and transparent at its end (nine stops). Drawn over
   * the art's edge on the plain ground, it is the art melting into the ground
   * — ADR-0016's immersive left and bottom melts, the full-bleed left gradient,
   * and the left scrim over a full-screen picture are all this ramp over their
   * own box. (adr_0002: a quadratic ramp back-loaded its opacity; rejected.)
   */
  static edgeMeltGradient(color: string, direction: 'to right' | 'to left' | 'to bottom' | 'to top'): string {
    const stops = Array.from({ length: 9 }, (_, i) => i / 8).map((t) => {
      const alpha = 1 - AppPreviewUtils.smoothstep(t);
      return `${AppPreviewUtils.withAlpha(color, Number(alpha.toFixed(3)))} ${t * 100}%`;
    });
    return `linear-gradient(${direction}, ${stops.join(', ')})`;
  }
}
